import { match, P } from 'ts-pattern'

import { developerLogGroup } from 'lib/developerLog'
import type {
  AutomationAnalysis,
  DependencyResolutionRunOutcome,
  SimulationExpression,
  SimulationResult
} from '../../simulation.types'
import type { ExpressionEvaluation } from '../evaluateExpression'
import { taskExecutor } from '../analyseAutomation/taskExecutor'

/** The badge every one of these lines carries, so the console can be filtered. */
export const SIMULATION_LOG_LABEL = 'Workflow simulation'

/**
 * Everything one run worked out, in a single block: the routes we read out of
 * the workflow, the data we fetched for them, and where it landed.
 *
 * One block per run rather than a line per stage, because the three only mean
 * anything together - a prediction that looks wrong is almost always explained
 * by which route matched given what came back.
 */
export const logSimulationRun = ({
  analysis,
  resolution,
  result,
  expressionResults
}: {
  analysis: AutomationAnalysis | undefined
  resolution: DependencyResolutionRunOutcome | undefined
  result: SimulationResult
  expressionResults: Record<string, ExpressionEvaluation>
}) =>
  developerLogGroup({
    label: SIMULATION_LOG_LABEL,
    summary: summarise(result),
    entries: [
      {
        label: 'Analysis',
        value: () => (analysis ? describeAnalysis(analysis) : 'none')
      },
      {
        label: 'Dependencies',
        value: () => (resolution ? describeResolution(resolution) : 'none')
      },
      {
        label: 'Evaluation',
        value: () => describeEvaluation({ result, expressionResults })
      }
    ]
  })

/** The one line you get without unfolding: where this quote ends up. */
export const summarise = (result: SimulationResult): string =>
  match(result)
    .with(
      { outcome: 'RESOLVED' },
      ({ terminalType }) => `Resolved - ${terminalType}`
    )
    .with({ outcome: 'INDETERMINATE' }, ({ reason }) => `No answer - ${reason}`)
    .exhaustive()

/**
 * The routes we found and the data they read, flattened enough to be read in a
 * console. `analysis` itself nests several layers deep, so printed whole it's a
 * pile of collapsed triangles rather than something you can scan.
 */
export const describeAnalysis = (analysis: AutomationAnalysis) => ({
  paths: analysis.paths.map(path => ({
    terminal: path.terminalType,
    requires: path.expressions.map(describeExpression),
    dependencies: path.dependencies.map(
      dependency => dependency.task.taskReferenceName
    ),
    unsupportedDependencies: path.unsupportedDependencies
  })),
  dependencies: analysis.dependencies.map(dependency => ({
    task: dependency.task.taskReferenceName,
    executor: taskExecutor(dependency.task),
    semanticTypes: dependency.semanticTypes
  }))
})

/**
 * One switch a route needs to have gone its way, and enough of the switch to
 * tell what it was asked.
 *
 * `isValueParam` is what says how to read `expression`: on one of those it's
 * the name of one of the values under `reads` rather than code, and whatever
 * sits under that name is the switch's answer. Worth having in front of you,
 * because a name and a one-line condition look much the same in a console.
 */
const describeExpression = (expression: SimulationExpression) => ({
  switch: expression.switchTaskReferenceName,
  group: expression.result ?? 'ELSE',
  order: expression.order,
  isValueParam: expression.isValueParam,
  expression: expression.expression,
  cases: expression.decisionCaseKeys,
  reads: expression.switchInputParameters
})

/**
 * How the round of fetching went. `fetched` lists the tasks that came back,
 * which is usually the first thing you want when a prediction looks wrong: it
 * says whether the quote was asked about again at all.
 */
export const describeResolution = (
  resolution: DependencyResolutionRunOutcome
) =>
  match(resolution)
    .with({ taskOutputs: P.select() }, taskOutputs => ({
      fetched: Object.keys(taskOutputs),
      outputs: taskOutputs
    }))
    .with({ reason: P.select() }, reason => ({ stopped: reason }))
    .exhaustive()

/**
 * Where the run landed, and what each switch answered on the way. The answers
 * are the part that explains the outcome: a route applies when every one of its
 * switches went the way it needed.
 */
export const describeEvaluation = ({
  result,
  expressionResults
}: {
  result: SimulationResult
  expressionResults: Record<string, ExpressionEvaluation>
}) => ({
  ...match(result)
    .with({ outcome: 'RESOLVED' }, ({ terminalType, path }) => ({
      outcome: 'RESOLVED',
      terminal: terminalType,
      took: path.expressions.map(describeExpression)
    }))
    .with({ outcome: 'INDETERMINATE' }, ({ reason }) => ({
      outcome: 'INDETERMINATE',
      reason
    }))
    .exhaustive(),
  answers: Object.fromEntries(
    Object.entries(expressionResults).map(
      ([switchTaskReferenceName, value]) => [
        switchTaskReferenceName,
        'failed' in value ? 'could not be run' : value.value
      ]
    )
  )
})
